import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';
import {
  buildCivilDate,
  civilDateParts,
  daysInMonth,
  formatCivilDate,
  maskCivilDate,
  parseCivilDate,
  type CivilDate,
} from './civil-date.js';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

export interface DatePickerProps {
  label: string;
  value: CivilDate | null;
  onValueChange: (value: CivilDate | null) => void;
  /// Data civil usada como "hoje" — quem decide é o fuso do negócio, não o do navegador.
  today: CivilDate;
  error?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/// Seletor de data civil: campo com máscara DD/MM/AAAA para quem digita rápido e calendário para
/// quem prefere apontar. Não usa o seletor nativo do navegador — ele muda de aparência e de idioma
/// a cada plataforma e não respeita o tema do sistema.
export function DatePicker({
  label,
  value,
  onValueChange,
  today,
  error,
  hint,
  disabled,
  className,
}: DatePickerProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatCivilDate(value));
  const [cursor, setCursor] = useState(() => civilDateParts(value ?? today));
  const [syncedValue, setSyncedValue] = useState(value);
  const fieldRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  // Enquanto se digita, o texto parcial é a fonte; quando o valor muda por fora, o campo acompanha.
  // O ajuste acontece no render, não num efeito, para não renderizar uma vez com o texto velho.
  if (value !== syncedValue) {
    setSyncedValue(value);
    setText(formatCivilDate(value));
    if (value) setCursor(civilDateParts(value));
  }

  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const total = daysInMonth(cursor.year, cursor.month);
  const firstWeekday = new Date(Date.UTC(cursor.year, cursor.month - 1, 1)).getUTCDay();
  const days = Array.from({ length: total }, (_item, index) => index + 1);

  function shiftMonth(step: number) {
    const total = cursor.year * 12 + (cursor.month - 1) + step;
    setCursor({ year: Math.floor(total / 12), month: (total % 12) + 1, day: cursor.day });
  }

  function commit(next: string) {
    setText(maskCivilDate(next));
    const parsed = parseCivilDate(next);
    // Apagar o campo limpa a data; enquanto ela estiver incompleta o valor anterior não é destruído.
    if (parsed) onValueChange(parsed);
    else if (next.replace(/\D/gu, '') === '') onValueChange(null);
  }

  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      <label htmlFor={id} className="block text-xs font-semibold text-content/70">
        {label}
      </label>
      <div className="relative min-w-0" ref={fieldRef}>
        <input
          id={id}
          value={text}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD/MM/AAAA"
          onChange={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={descriptionId}
          className={cn(
            'hue-input h-12 w-full min-w-0 rounded-xl border bg-surface-elevated pl-4 pr-12 text-sm tabular-nums text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-danger/70' : 'border-surface-border',
          )}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          aria-label={`Abrir calendário de ${label.toLocaleLowerCase('pt-BR')}`}
          aria-expanded={open}
          aria-controls={`${id}-calendar`}
          className="absolute right-1 top-1/2 grid size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CalendarDays className="size-4" />
        </button>

        {open ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={close}
            id={`${id}-calendar`}
            className="w-[19rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                aria-label="Mês anterior"
                className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <ChevronLeft className="size-4" />
              </button>
              <strong aria-live="polite" className="text-xs font-semibold">
                {MONTHS[cursor.month - 1]} de {cursor.year}
              </strong>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                aria-label="Próximo mês"
                className="grid size-9 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1" role="group" aria-label={`Dias de ${label}`}>
              {WEEKDAYS.map((weekday, index) => (
                <span
                  key={`${weekday}-${index}`}
                  aria-hidden="true"
                  className="grid h-8 place-items-center text-[10px] font-semibold uppercase text-content-faint"
                >
                  {weekday}
                </span>
              ))}
              {Array.from({ length: firstWeekday }, (_item, index) => (
                <span key={`empty-${index}`} />
              ))}
              {days.map((day) => {
                const candidate = buildCivilDate(cursor.year, cursor.month, day);
                const selected = candidate === value;
                const isToday = candidate === today;
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={selected}
                    aria-current={isToday ? 'date' : undefined}
                    onClick={() => {
                      onValueChange(candidate);
                      setText(formatCivilDate(candidate));
                      setOpen(false);
                    }}
                    className={cn(
                      'grid h-9 cursor-pointer place-items-center rounded-lg text-xs tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
                      selected
                        ? 'bg-brand-500 font-semibold text-on-brand'
                        : 'text-content-secondary hover:bg-surface-elevated hover:text-content',
                      !selected && isToday && 'border border-brand-500/40 font-semibold',
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between gap-2 border-t border-surface-border pt-2">
              <button
                type="button"
                onClick={() => {
                  onValueChange(null);
                  setText('');
                  setOpen(false);
                }}
                className="min-h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                Limpar
              </button>
              <button
                type="button"
                onClick={() => {
                  onValueChange(today);
                  setText(formatCivilDate(today));
                  setOpen(false);
                }}
                className="min-h-9 cursor-pointer rounded-lg px-3 text-xs font-semibold text-brand-400 transition hover:bg-brand-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                Hoje
              </button>
            </div>
          </FloatingPanel>
        ) : null}
      </div>
      {error ? (
        <p id={descriptionId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
